// Der Wächter gegen die anderen Gegner — wen er greift und wie stark er bremst.
//
// **Reine Regeln, kein Zustand.** Wer gerade brennt und seit wann, führt online
// der Abweichungsspeicher des Servers und solo `HostileField`. Spiegel von
// `backend/farmining/world/wardenfire.py`.
//
// **Der Befund, der das Ganze einfach macht:** IDs und Positionen der Gegner
// einer Kachel hängen **nicht** von `built` ab — nur ihre Art. `chunkHostiles`
// zieht `x`, `y` und `pick` **vor** der Eignungsprüfung, und die Reihenfolge
// der Ziehungen ist Vertrag (Gates FG-20, FG-30). Eine Auswahl über ID und Ort
// ist damit beobachterunabhängig; eine über `maxHp` wäre es nicht.
//
// Daraus folgt alles Weitere: für die Auswahl braucht es **keine Nachricht**,
// sein Schaden ist eine **Zeit** statt eines Trefferpunktwerts, und gesucht
// wird mit `built = 0` — nicht als Näherung, sondern weil das genau die
// Auskunft ist, die für alle gilt.
//
// **Das Suchen selbst steht in `hostiles.ts`** (`victimsInReach`), nicht hier:
// es braucht `hostilesInChunk`, und ein Import von dort zurück wäre ein Zyklus
// (Gate FG-05). Hier bleibt, was ohne die Gegnertabelle auskommt — dieselbe
// Aufteilung wie bei `guided.ts`, und aus demselben Grund.

import { balancing } from '../data/balancing';

/**
 * Was das Feuer des Wächters von einem Opfer braucht — **mehr nicht**.
 *
 * Dieselbe Sparsamkeit wie bei `GuidedTarget` und `ChainFrom`: eine ID für die
 * Klebrigkeit, ein Ort für die Reichweite. Eine Hülle gehört ausdrücklich
 * **nicht** dazu — sie hängt am `built` des Betrachters, und damit räumte
 * derselbe Wächter für jeden Spieler etwas anderes weg.
 */
export interface WardenTarget {
  id: string;
  x: number;
  y: number;
}

/**
 * Wen er als nächstes greift — **das laufende Ziel bleibt, solange es lebt**.
 *
 * Klebrigkeit aus demselben Grund wie bei den Drohnen: ohne sie wechselte er
 * bei jedem Schritt, sobald zwei Gegner fast gleich weit weg stehen, und keiner
 * erreichte je die Brenndauer. Er räumte dann gar nichts ab, und niemand bekäme
 * eine Meldung.
 *
 * Die Liste ist bereits geordnet (`victimsInReach`) — hier wird nicht sortiert.
 */
export function pickVictim<T extends WardenTarget>(
  candidates: readonly T[], currentId: string,
): T | null {
  if (currentId) {
    for (const hostile of candidates) {
      if (hostile.id === currentId) return hostile;
    }
  }
  return candidates.length ? candidates[0] : null;
}

/**
 * Wie viele Gegner ihn beschäftigen — geklemmt auf `wardenSuppressMax`.
 *
 * Eine **Zahl von Gegnern, kein Schaden**: die Hülle eines Gegners hängt am
 * `built` des Betrachters, ein Schadenswert wäre damit beobachterabhängig.
 */
export function suppression(livingCount: number): number {
  return Math.max(0, Math.min(balancing().warden.suppressMax, livingCount));
}

/**
 * Der Anteil seiner Heilung, der bei so vielen Unterdrückern noch ankommt.
 *
 * **Der Riegel ist die Bauweise, nicht der Grenzwert.** Die Größe, die die
 * Gegner beeinflussen, steht hier als **positiver Faktor** auf die Heilung —
 * es gibt keine Rechnung, in der seine Hülle durch KI-Feuer sinkt. Fiele er
 * ohne Spieler-Kill, blieben 29 Baumknoten zu, obwohl jemand nur zugesehen hat;
 * der Beitragspool hätte einen Rest, der niemandem gehört; und er wäre 90
 * Sekunden weg.
 *
 * Bei `suppressMax` ist die Heilung ganz aus — **nicht** negativ.
 */
export function healFactor(suppressors: number): number {
  const max = balancing().warden.suppressMax;
  if (max <= 0) return 1;
  return Math.max(0, 1 - suppression(suppressors) / max);
}
